/**
 * Alert Service — API client for the unified alert system
 * TASK-381: Communicates with alert-service via gateway proxy
 */

import { httpClient } from '../lib/httpClient';
import type { DashboardAlert, AlertCounts } from '../types/dashboard';

export interface AlertListResponse {
  alerts: DashboardAlert[];
  total: number;
  unread_count: number;
}

export interface AlertListParams {
  level?: string;
  category?: string;
  status?: string;
  client_id?: string;
  limit?: number;
  offset?: number;
  sort?: string;
  /**
   * TASK-801: return exactly the population `/counts` counts — still outstanding (active,
   * read, or an expired snooze) AND from a source the RM can dispose of. `status` cannot
   * express this: it takes one value and unresolved spans three states.
   */
  unresolved?: boolean;
}

export const alertService = {
  list: (params?: AlertListParams): Promise<AlertListResponse> =>
    httpClient.get('/api/alerts', { params }),

  getCounts: (): Promise<AlertCounts> =>
    httpClient.get('/api/alerts/counts'),

  markAsRead: (id: string): Promise<DashboardAlert> =>
    httpClient.patch(`/api/alerts/${id}/read`),

  dismiss: (id: string): Promise<DashboardAlert> =>
    httpClient.patch(`/api/alerts/${id}/dismiss`),

  snooze: (id: string, until: string): Promise<DashboardAlert> =>
    httpClient.patch(`/api/alerts/${id}/snooze`, { snoozed_until: until }),

  /**
   * TASK-732: the policy-valid acceptance — the one way a person closes a condition the
   * evaluators cannot clear (e.g. a screening false positive). The server decides who may
   * do this and whether the rationale is mandatory; a refusal returns a structured
   * {code, policy_id, action, reason} detail.
   */
  resolve: (id: string, rationale?: string): Promise<DashboardAlert> =>
    httpClient.patch(`/api/alerts/${id}/resolve`, { rationale }),
  // TASK-588: get / bulkDismiss removed with their dead backend endpoints.
  // (resolve re-added by TASK-732 with a policy-enforced backend route.)
};
